import React from 'react';
import { useTranslation } from 'react-i18next';

export default function BotStatusBanner({ config, runtime, playbooks, onActivate }) {
  const { t } = useTranslation('bots');
  // El funnel ACTIVO es el que apunta runtime.activePlaybookId y existe.
  // (Antes se tomaba "el primero con states": el banner decía Activo con el
  // puntero roto y escondía el botón de activar justo cuando hacía falta.)
  const activePlaybook = playbooks.find(p => p.id === runtime?.activePlaybookId) || null;
  const hasFunnel = playbooks.some(p => p.states && Object.keys(p.states).length > 0);

  // Checklist de activación - valores por defecto cuentan como configurados
  const checks = [
    {
      id: 'name',
      label: t('status.nameConfigured'),
      done: !!(config?.name && config.name.trim())
    },
    {
      id: 'purpose',
      label: t('status.purposeDefined'),
      done: !!(config?.purpose && config.purpose.trim())
    },
    {
      id: 'funnel',
      label: t('status.funnelActive'),
      done: hasFunnel
    },
    {
      id: 'tone',
      label: t('status.toneConfigured'),
      done: config?.tone !== ''
    },
    {
      id: 'language',
      label: t('status.languageSelected'),
      done: config?.language !== ''
    },
  ];

  const allChecksPassed = checks.every(c => c.done);
  const isActive = !!activePlaybook && allChecksPassed;

  return (
    <div className={`status-banner ${isActive ? 'active' : 'inactive'}`}>
      <div className="status-header">
        <div className="status-indicator">
          <span className={`dot ${isActive ? 'green' : 'gray'}`}></span>
          <span className="status-text">
            {isActive ? t('status.botActive') : t('status.botInactive')}
          </span>
        </div>

        {!isActive && allChecksPassed && (
          <button onClick={onActivate} className="btn-activate">
            {t('status.activateBot')}
          </button>
        )}
      </div>

      {!isActive && (
        <div className="checklist">
          <p className="checklist-title">{t('status.completeSteps')}</p>
          <div className="checklist-items">
            {checks.map(check => (
              <div key={check.id} className="check-item">
                <span className={`check-icon ${check.done ? 'done' : ''}`}>
                  {check.done ? '✓' : '○'}
                </span>
                <span className={check.done ? 'done' : ''}>{check.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {isActive && runtime && (
        <div className="runtime-info">
          <p>
            <strong>{t('status.currentState')}</strong> {runtime.currentState}
          </p>
          <p>
            <strong>{t('status.turns')}</strong> {runtime.turnCount || 0}
          </p>
          {activePlaybook && (
            <p>
              <strong>{t('status.funnel')}</strong> {activePlaybook.name}
            </p>
          )}
        </div>
      )}

      <style>{`
        .status-banner {
          background: white;
          border-radius: 8px;
          padding: 1.5rem;
          margin-bottom: 2rem;
          border-left: 4px solid #cbd5e0;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }

        .status-banner.active {
          border-left-color: #48bb78;
          background: #f0fff4;
        }

        .status-banner.inactive {
          border-left-color: #a0aec0;
        }

        .status-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
        }

        .status-indicator {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .dot {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          display: inline-block;
        }

        .dot.green {
          background: #48bb78;
          box-shadow: 0 0 8px rgba(72, 187, 120, 0.5);
        }

        .dot.gray {
          background: #a0aec0;
        }

        .status-text {
          font-size: 1.125rem;
          font-weight: 600;
          color: #2d3748;
        }

        .btn-activate {
          padding: 0.5rem 1.5rem;
          background: #48bb78;
          color: white;
          border: none;
          border-radius: 6px;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.2s;
        }

        .btn-activate:hover {
          background: #38a169;
        }

        .checklist {
          margin-top: 1rem;
          padding-top: 1rem;
          border-top: 1px solid #e2e8f0;
        }

        .checklist-title {
          font-size: 0.875rem;
          color: #718096;
          margin-bottom: 0.75rem;
          font-weight: 600;
        }

        .checklist-items {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 0.75rem;
        }

        .check-item {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.875rem;
        }

        .check-icon {
          font-weight: bold;
          color: #a0aec0;
        }

        .check-icon.done {
          color: #48bb78;
        }

        .check-item span.done {
          color: #48bb78;
          text-decoration: line-through;
        }

        .runtime-info {
          margin-top: 1rem;
          padding-top: 1rem;
          border-top: 1px solid #c6f6d5;
          font-size: 0.875rem;
          color: #2d3748;
        }

        .runtime-info p {
          margin: 0.25rem 0;
        }

        .runtime-info strong {
          color: #22543d;
        }
      `}</style>
    </div>
  );
}
